import React, { useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Button, ButtonToolbar, Glyphicon } from "react-bootstrap";
import Swal from "sweetalert2";
import toastr from "toastr";

import usePermission from "provider/PermissionProvider";
import { CONFIGURATION_KEY } from "provider/PermissionProvider/permissions";

import { useDispatch } from "react-redux";

import { RemoteGrid } from "../../Grid";
import { Panel, PanelHeader } from "../../Panel";
import CopyModal from "./copy-modal";
import PriorityModal from "./priority-modal";
import { CompareValues } from "../../../helper";

export function Configurations({
  configs,
  selectedUnit,
  onToggleActivityLogs,
  onFetchConfiguration,
  onCopyConfiguration,
  onExportConfiguration,
  onImportConfiguration,
  onDeleteConfiguration,
  onSavePriority,
  isLoading,
  onHandleTableChange,
  onHandleSearch,
  pagination,
}) {
  const dispatch = useDispatch();
  const columns = [
    {
      dataField: "id",
      text: "Id",
      hidden: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "name",
      text: "Config Name",
      sort: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "unitFamily",
      text: "Family",
      sort: false,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "unitType",
      text: "Type",
      sort: false,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "unitVersion",
      text: "Version",
      sort: false,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "buttons",
      text: "",
      formatter: (cell, row) => (
        <ButtonToolbar>
          <Button
            bsStyle="primary"
            title="View Configuration and Recipe"
            onClick={() => handleViewDetails(row)}
          >
            <Glyphicon glyph="eye-open" />
          </Button>
          <Button
            bsStyle="primary"
            title="Create Copy"
            onClick={() => handleCopyConfiguration(row)}
            disabled={!row.isEditable}
          >
            <Glyphicon glyph="duplicate" />
          </Button>
          <Button
            bsStyle="primary"
            title="Edit Configuration"
            onClick={() => handleEditConfiguration(row)}
            disabled={!row.isEditable}
          >
            <Glyphicon glyph="pencil" />
          </Button>
          <Button
            bsStyle="primary"
            title="Export Configuration"
            onClick={() => handleExportConfiguration(row)}
            disabled={!row.isEditable}
          >
            <Glyphicon glyph="download-alt" />
          </Button>
          <Button
            bsStyle="danger"
            title="Delete Configuration"
            onClick={() => handleDeleteConfiguration(row)}
            disabled={!row.isEditable}
          >
            <Glyphicon glyph="trash" />
          </Button>
        </ButtonToolbar>
      ),
    },
  ];

  const canAddConfiguration =
    selectedUnit.family !== -1 && selectedUnit.type !== -1;
  const canManagePriority =
    selectedUnit.family !== -1 &&
    selectedUnit.type !== -1 &&
    configs.items.length > 1; //enable if the length of array is greater than 2.
  const [selectedConfiguration, setSelectedConfiguration] = useState({});
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const history = useHistory();
  const hiddenFileInput = useRef(null);
  const { permissionContext } = usePermission();
  const { hasAtLeastOneFullPermission, hasFullPermission } =
    permissionContext.permissions;

  function handleAddConfiguration() {
    history.push({
      pathname: "/configuration/setup",
      state: {
        selectedUnitFamily: selectedUnit.family,
        selectedUnitType: selectedUnit.type,
        selectedUnitVersion: selectedUnit.version,
      },
    });
  }

  function handleViewDetails(row) {
    history.push({
      pathname: `/configuration/details/${row.id}`,
      configName: row.name,
    });
  }

  function toggleCopyModal() {
    setShowCopyModal(!showCopyModal);
  }

  function handleCopyConfiguration(row) {
    setSelectedConfiguration(row);
    toggleCopyModal();
  }

  function handleSubmitCopyConfiguration(config) {
    dispatch(
      onCopyConfiguration(config.configuration.id, config.configName)
    ).then(async (response) => {
      if (response.isSuccess) {
        await onFetchConfiguration();
        toggleCopyModal();
        toastr.info(
          `Configuration ${config.configuration.name} has been copied successfully.`,
          "Copy"
        );
      } else {
        toastr.error(`${response.payload.error}`, "Error");
      }
    });
  }

  function handleEditConfiguration(row) {
    history.push({
      pathname: `/configuration/setup/${row.id}`,
    });
  }

  function handleDeleteConfiguration(row) {
    Swal.fire({
      title: "Delete configuration?",
      text: `Do you really want to delete '${row.name}'?`,
      type: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No",
      reverseButtons: true,
      focusConfirm: false,
      focusCancel: true,
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          return await dispatch(onDeleteConfiguration(row.id));
        } catch (error) {
          Swal.fire(
            "An error has occurred",
            "An unexpected error has occurred. Please try again later.",
            "error"
          );
        }
      },
    }).then(async (response) => {
      if (response.value) {
        if (response.value.isSuccess) {
          Swal.fire("Deleted!", response.value.payload.message, "success");
          await onFetchConfiguration();
        } else {
          Swal.fire(
            "An error has occurred",
            response.value.payload.error,
            "error"
          );
        }
      }
    });
  }

  async function handleExportConfiguration(row) {
    await dispatch(onExportConfiguration(row.id, row.name));
  }

  function handleImportConfiguration() {
    hiddenFileInput.current.click();
  }

  async function handleFileInputChange(event) {
    const blobFile = event.target.files[0];
    var formData = new FormData();
    formData.append("file", blobFile);

    const response = await dispatch(onImportConfiguration(formData));

    if (response.isSuccess) {
      await onFetchConfiguration();
      toastr.info(`Configuration has been imported successfully.`, "Import");
    } else {
      toastr.error(`${response.payload.error}`, "Error");
    }
  }

  function handleClosePriorityModal() {
    setShowPriorityModal(false);
  }

  async function handleSavePriority(items) {
    let response = await onSavePriority(items);
    if (response.isSuccess === true) {
      toastr.info(`Priority sucesfully saved.`, "Configuration");
    }
    handleClosePriorityModal();
    await onFetchConfiguration();
  }

  return (
    <Panel>
      <PanelHeader>
        <ButtonToolbar>
          <Button
            bsSize="small"
            bsStyle="success"
            disabled={
              !canAddConfiguration ||
              !hasAtLeastOneFullPermission(CONFIGURATION_KEY) ||
              !hasFullPermission(selectedUnit.family, CONFIGURATION_KEY)
            }
            onClick={handleAddConfiguration}
          >
            <Glyphicon glyph="plus" /> Add Configuration
          </Button>
          <Button
            bsSize="small"
            bsStyle="success"
            disabled={
              !canManagePriority ||
              !hasAtLeastOneFullPermission(CONFIGURATION_KEY) ||
              !hasFullPermission(selectedUnit.family, CONFIGURATION_KEY)
            }
            onClick={() => setShowPriorityModal(true)}
          >
            <Glyphicon glyph="sort" /> Order Priority
          </Button>
          <Button
            bsSize="small"
            bsStyle="primary"
            onClick={onToggleActivityLogs}
          >
            <Glyphicon glyph="tasks" /> View Activity Logs
          </Button>
          <Button
            bsSize="small"
            bsStyle="primary"
            onClick={handleImportConfiguration}
            disabled={!hasAtLeastOneFullPermission(CONFIGURATION_KEY)}
          >
            <Glyphicon glyph="upload" /> Import Configuration
          </Button>
          <input
            id="file"
            type="file"
            style={{ display: "none" }}
            ref={hiddenFileInput}
            accept=".config"
            onChange={handleFileInputChange}
          />
        </ButtonToolbar>
      </PanelHeader>

      <div className="panel-body">
        <RemoteGrid
          data={configs}
          columns={columns}
          loading={isLoading}
          pagination={pagination}
          onSearch={onHandleSearch}
          onTableChange={onHandleTableChange}
          placeHolder="Enter config name"
        />
      </div>
      <CopyModal
        configuration={selectedConfiguration}
        show={showCopyModal}
        onHide={toggleCopyModal}
        onSave={handleSubmitCopyConfiguration}
      />

      <PriorityModal
        show={showPriorityModal}
        onHide={handleClosePriorityModal}
        onSavePriority={handleSavePriority}
        items={(() => {
          const items = [...configs.items];
          //sort array order by priority.
          items.sort(CompareValues("priority", "asc"));
          return items.map((item) => {
            return { id: item.id, name: item.name };
          });
        })()}
      />
    </Panel>
  );
}
